// usePlayback.js
// Shared timeline playback engine for the passive-asset map views. Given the
// number of points, it manages the current index, play/pause, and speed, and
// exposes Reset / Back / Step / Play controls. Base cadence is ~600ms/point at
// 1x; speed scales the tick interval.

import { useState, useRef, useEffect, useCallback } from 'react';

export default function usePlayback(count) {
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const timerRef = useRef(null);

  // Reset whenever the underlying data set size changes.
  useEffect(() => {
    setIndex(0);
    setIsPlaying(false);
  }, [count]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (!isPlaying) return undefined;
    if (count <= 1) {
      setIsPlaying(false);
      return undefined;
    }
    const tick = Math.max(60, Math.round(600 / Math.max(0.25, Number(speed) || 1)));
    timerRef.current = setInterval(() => {
      setIndex((i) => {
        if (i >= count - 1) {
          setIsPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, tick);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, speed, count]);

  const reset = useCallback(() => {
    setIsPlaying(false);
    setIndex(0);
  }, []);

  const back = useCallback(() => {
    setIsPlaying(false);
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const step = useCallback(() => {
    setIsPlaying(false);
    setIndex((i) => Math.min(count - 1, i + 1));
  }, [count]);

  const toggle = useCallback(() => {
    if (count === 0) return;
    setIndex((i) => (i >= count - 1 ? 0 : i));
    setIsPlaying((v) => !v);
  }, [count]);

  return { index, setIndex, isPlaying, setIsPlaying, speed, setSpeed, reset, back, step, toggle };
}
